// Signed document adjustments: expenses are positive, discounts are negative.
// Distributed amounts are already embedded in line prices and must not be added twice.
const number = (value) => Number(String(value ?? "").replace(",", "."));
const round = (value) => Number(value.toFixed(8));
let nextLineKey = 0;
export const isPercentageAdjustment = (entry) => entry?.discount_type === "percentage"
  || Object.prototype.hasOwnProperty.call(entry?.allocation || {}, "percentage");
export const adjustmentPercentage = (entry) => entry?.percentage ?? entry?.allocation?.percentage ?? "";
export const adjustmentKind = (entry) => isPercentageAdjustment(entry) || number(entry.amount) < 0 || entry.kind === "discount" ? "discount" : "expense";
const keyOf = (line, index) => String(line.id ?? line.temp_id ?? line.adjustment_key ?? `row-${index}`);
const itemOf = (line) => line.item_id ?? line.suggested_item_id ?? line.suggested_item?.id;
const hasValue = (value) => String(value ?? "").trim() !== "";
const percentageOf = (entry) => {
  const percentage = number(adjustmentPercentage(entry));
  if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
    throw new Error("Enter a discount percentage greater than 0 and no more than 100.");
  }
  return percentage;
};

export function prepareDeliveryNoteAdjustments(entries, lines, { getItemId = itemOf } = {}) {
  return entries.flatMap((entry) => {
    const percentage = isPercentageAdjustment(entry);
    let amount = number(entry.amount);
    if (percentage) {
      if (!hasValue(adjustmentPercentage(entry)) && !hasValue(entry.amount) && !entry.distribute_cost && !entry.allocation) return [];
      const value = percentageOf(entry);
      if (!entry.distribute_cost || !Array.isArray(entry.allocation?.entries) || !entry.allocation.entries.length
        || !["all", "line"].includes(entry.allocation.mode) || !hasValue(entry.allocation.percentage)) {
        throw new Error("Apply the percentage discount before saving.");
      }
      if (value !== number(entry.allocation.percentage)) throw new Error("Undo the percentage discount before changing its percentage.");
      // The persisted document amount has two decimals; allocation snapshots retain exact changes.
      if (entry.allocation.entries.some((part) => !hasValue(part.amount) || !Number.isFinite(number(part.amount)) || number(part.amount) > 0)) {
        throw new Error("Enter a valid adjustment amount.");
      }
      amount = round(entry.allocation.entries.reduce((sum, part) => sum + number(part.amount), 0));
    } else if (!hasValue(entry.amount) || amount === 0) return [];
    if (!Number.isFinite(amount)) throw new Error("Enter a valid adjustment amount.");
    const allocation = entry.allocation ? { ...entry.allocation, entries: entry.allocation.entries.map((part) => {
      const index = lines.findIndex((line, i) => keyOf(line, i) === String(part.line_id ?? part.key));
      if (index < 0 || String(getItemId(lines[index])) !== String(part.item_id)) {
        throw new Error("Undo the distribution before removing or replacing an affected item.");
      }
      return { ...part, line_index: index };
    }) } : null;
    if (percentage) allocation.percentage = percentageOf(entry);
    return [{ description: String(entry.description || "").trim() || (percentage || amount < 0 ? "Discount" : "Expense"),
      amount, distribute_cost: Boolean(entry.distribute_cost), ...(allocation ? { allocation } : {}) }];
  });
}

export function distributeAdjustment(lines, entry, { mode = "all", lineIndex = null, quantityKey = "quantity_in_dn", priceKey = "unit_price_in_dn", getItemId = itemOf } = {}) {
  if (entry.distribute_cost) return { lines, entry };
  const percentage = isPercentageAdjustment(entry) ? percentageOf(entry) : null;
  if (percentage !== null && ((mode !== "all" && mode !== "line")
    || (mode === "line" && (!Number.isInteger(lineIndex) || lineIndex < 0 || lineIndex >= lines.length)))) {
    throw new Error("Apply the percentage discount to all items or one item.");
  }
  const amount = number(entry.amount);
  if (percentage === null && (!Number.isFinite(amount) || !amount)) throw new Error("Enter a valid adjustment amount.");
  const identifiedLines = lines.map((line) => line.id != null || line.temp_id != null || line.adjustment_key
    ? line : { ...line, adjustment_key: `adjustment-${Date.now()}-${++nextLineKey}` });
  const eligible = identifiedLines.map((line, index) => ({ line, index, total: number(line[quantityKey]) * number(line[priceKey]) }))
    .filter(({ line, index, total }) => (mode === "all" || index === lineIndex) && number(line[quantityKey]) > 0 && Number.isFinite(total) && total > 0 && !line.is_free_stock);
  const total = eligible.reduce((sum, row) => sum + row.total, 0);
  if (!eligible.length) throw new Error("Choose an item with a positive quantity and price.");
  if (percentage === null && amount < 0 && -amount > total + 0.00000001) throw new Error("The discount cannot exceed the selected items' value.");
  let shares = eligible;
  if (percentage === null) {
    // Fixed amounts allocate cents; percentages change every price by the same rate.
    const cents = Math.round(Math.abs(amount) * 100);
    shares = eligible.map((row) => ({ ...row, raw: cents * row.total / total, cents: Math.floor(cents * row.total / total) }));
    const remainder = cents - shares.reduce((sum, row) => sum + row.cents, 0);
    [...shares].sort((a, b) => (b.raw - b.cents) - (a.raw - a.cents) || a.index - b.index).slice(0, remainder).forEach((row) => { row.cents += 1; });
  }
  const parts = [];
  const nextLines = identifiedLines.map((line, index) => {
    const share = shares.find((row) => row.index === index);
    if (!share) return line;
    const quantity = number(line[quantityKey]);
    const after = percentage === null ? round((share.total + Math.sign(amount) * share.cents / 100) / quantity)
      : round(number(line[priceKey]) * (1 - percentage / 100));
    const delta = percentage === null ? Math.sign(amount) * share.cents / 100 : round(quantity * (after - number(line[priceKey])));
    if (after < 0) throw new Error("The discount cannot exceed the selected items' value.");
    const key = keyOf(line, index);
    parts.push({ key, line_id: line.id || null, item_id: getItemId(line), amount: delta,
      quantity, unit_level: line.unit_level, before: line[priceKey], after,
      before_price_status: line.price_status || "confirmed" });
    return { ...line, adjustment_key: key, [priceKey]: String(after), total_price_in_dn: round(quantity * after),
      price_status: after === 0 ? "confirmed" : line.price_status || "confirmed", is_free_stock: after === 0,
      free_stock_reason: after === 0 ? "Fully discounted" : "" };
  });
  const percentageAmount = percentage !== null ? round(parts.reduce((sum, part) => sum + part.amount, 0)) : null;
  if (percentage !== null && percentageAmount >= 0) throw new Error("The percentage is too small to change the selected prices.");
  return { lines: nextLines, entry: { ...entry, distribute_cost: true,
    ...(percentage !== null ? { discount_type: "percentage", percentage: adjustmentPercentage(entry), amount: percentageAmount } : {}),
    description: entry.description || (percentage !== null || amount < 0 ? "Discount" : "Expense"),
    allocation: { mode, entries: parts, ...(percentage !== null ? { percentage } : {}) } } };
}

export function undoAdjustment(lines, entry, { quantityKey = "quantity_in_dn", priceKey = "unit_price_in_dn" } = {}) {
  if (!entry.distribute_cost) return { lines, entry };
  const parts = entry.allocation?.entries;
  const total = lines.reduce((sum, line) => sum + number(line[quantityKey]) * number(line[priceKey]), 0);
  const nextLines = lines.map((line, index) => {
    const part = parts?.find((candidate) => String(candidate.line_id ?? candidate.key) === keyOf(line, index));
    if (parts && !part) return line;
    const quantity = number(line[quantityKey]);
    if (!(quantity > 0)) throw new Error("Restore a positive quantity before undoing this distribution.");
    const current = number(line[priceKey]);
    // Legacy expenses have no allocation snapshot; their only supported mode was proportional.
    const delta = part ? number(part.amount) / quantity : total > 0 ? current * number(entry.amount) / total : 0;
    const unchanged = part && quantity === number(part.quantity) && line.unit_level === part.unit_level && current === number(part.after);
    const price = unchanged ? part.before : round(current - delta);
    if (number(price) < 0) throw new Error("Undo later adjustments first to avoid a negative item price.");
    return { ...line, [priceKey]: price, total_price_in_dn: round(quantity * number(price)),
      price_status: part?.before_price_status || line.price_status || "confirmed", is_free_stock: number(price) === 0,
      free_stock_reason: number(price) === 0 ? line.free_stock_reason : "" };
  });
  return { lines: nextLines, entry: { ...entry, distribute_cost: false, allocation: null,
    ...(isPercentageAdjustment(entry) ? { discount_type: "percentage", percentage: adjustmentPercentage(entry), amount: "" } : {}) } };
}

// Do not infer persisted allocation identity from the item's name or row position:
// duplicate products and reordered OCR lines are both valid.
const stableKeyOf = (line) => {
  const key = line.id ?? line.temp_id ?? line.adjustment_key;
  return hasValue(key) ? String(key) : null;
};
const allocationKeyOf = (part) => {
  const key = part?.line_id ?? part?.key;
  return hasValue(key) ? String(key) : null;
};
const allocationIndex = (lines, part) => {
  const key = allocationKeyOf(part);
  if (key === null) return -1;
  const matches = lines.flatMap((line, index) => stableKeyOf(line) === key ? [index] : []);
  return matches.length === 1 ? matches[0] : -1;
};
const matchesAllocationSnapshot = (lines, entry, { quantityKey = "quantity_in_dn", priceKey = "unit_price_in_dn" } = {}) => {
  const parts = entry.allocation?.entries;
  const seen = new Set();
  return Array.isArray(parts) && parts.length > 0 && parts.every((part) => {
    const index = allocationIndex(lines, part);
    if (index < 0 || seen.has(index)) return false;
    seen.add(index);
    const line = lines[index];
    return hasValue(part.quantity) && number(part.quantity) > 0 && number(line[quantityKey]) === number(part.quantity)
      && String(line.unit_level ?? "") === String(part.unit_level ?? "")
      && hasValue(part.before) && Number.isFinite(number(part.before)) && number(part.before) >= 0
      && hasValue(part.after) && Number.isFinite(number(part.after)) && number(line[priceKey]) === number(part.after)
      && hasValue(part.amount) && Number.isFinite(number(part.amount));
  });
};

// The user can apply adjustment rows in any order. Snapshots identify the last
// applied adjustment even when it is not the last row in the expenses array.
const lastMatchingAdjustment = (lines, pending, expenses, options) => [...pending].reverse()
  .find((index) => matchesAllocationSnapshot(lines, expenses[index], options));

export function repairAppliedDiscountBindings(lines, expenses, options = {}) {
  const getItemId = options.getItemId || itemOf;
  const pending = expenses.flatMap((entry, index) => entry.distribute_cost ? [index] : []);
  let currentLines = lines;
  let repaired = expenses;
  while (pending.length) {
    const index = lastMatchingAdjustment(currentLines, pending, expenses, options);
    if (index === undefined) break; // No proof of unchanged commercial values; leave the saved data alone.
    const entry = expenses[index];
    if (adjustmentKind(entry) === "discount") {
      const snapshotLines = currentLines;
      const entries = entry.allocation.entries.map((part) => {
        const itemId = getItemId(snapshotLines[allocationIndex(snapshotLines, part)]);
        return hasValue(itemId) && String(itemId) !== String(part.item_id) ? { ...part, item_id: itemId } : part;
      });
      if (entries.some((part, partIndex) => part !== entry.allocation.entries[partIndex])) {
        if (repaired === expenses) repaired = [...expenses];
        repaired[index] = { ...entry, allocation: { ...entry.allocation, entries } };
      }
    }
    // Reverse only a proven snapshot, solely to inspect earlier adjustments.
    try {
      currentLines = undoAdjustment(currentLines, entry, options).lines;
    } catch {
      // This is read-only repair during render, not a reason to erase saved
      // discounts or fail to open a draft with malformed old allocation data.
      return expenses;
    }
    pending.splice(pending.indexOf(index), 1);
  }
  return repaired;
}

export function removeAppliedDiscounts(lines, expenses, options = {}) {
  const { editLines = (rows) => rows, quantityKey = "quantity_in_dn", priceKey = "unit_price_in_dn", getItemId = itemOf } = options;
  const isAppliedDiscount = (entry) => entry.distribute_cost && adjustmentKind(entry) === "discount";
  if (!expenses.some(isAppliedDiscount)) return { lines: editLines(lines), expenses, removed: false };
  const pending = expenses.flatMap((entry, index) => entry.distribute_cost ? [index] : []);
  const undone = [];
  let currentLines = lines;
  while (pending.length) {
    const index = lastMatchingAdjustment(currentLines, pending, expenses, options) ?? pending[pending.length - 1];
    const result = undoAdjustment(currentLines, expenses[index], options);
    currentLines = result.lines;
    undone.push({ index, entry: result.entry });
    pending.splice(pending.indexOf(index), 1);
  }
  // Quantities, units and article removal must change before retained expenses
  // are recalculated; otherwise their embedded amount would change with the edit.
  currentLines = editLines(currentLines);
  const retained = new Map();
  // Reapply only retained expenses, in their original application order, so an
  // expense snapshot never retains a discount in its "before" price.
  [...undone].reverse().forEach(({ index, entry }) => {
    if (isAppliedDiscount(expenses[index])) return;
    const original = expenses[index];
    const parts = original.allocation?.entries;
    const originalTargets = Array.isArray(parts) ? parts.map((part) => allocationIndex(currentLines, part)) : currentLines.map((line, lineIndex) => lineIndex);
    const targetIndexes = [...new Set(originalTargets.filter((target) => target >= 0 && hasValue(getItemId(currentLines[target]))))];
    // Replaying a subset preserves the original expense targets, including when
    // a removed 100% discount makes another product eligible again.
    const targets = targetIndexes.map((target) => currentLines[target]);
    if (!targets.some((line) => number(line[quantityKey]) > 0 && Number.isFinite(number(line[priceKey]))
      && number(line[priceKey]) > 0 && !line.is_free_stock)) {
      // Its only target may have been removed or made free. Keep the expense
      // amount as a separate document charge instead of blocking the edit.
      retained.set(index, entry);
      return;
    }
    const result = distributeAdjustment(targets, entry, { ...options, mode: "all" });
    currentLines = currentLines.map((line, lineIndex) => {
      const target = targetIndexes.indexOf(lineIndex);
      return target < 0 ? line : result.lines[target];
    });
    retained.set(index, { ...result.entry, allocation: { ...result.entry.allocation, mode: original.allocation?.mode || "all" } });
  });
  return { lines: currentLines, expenses: expenses.flatMap((entry, index) => isAppliedDiscount(entry) ? [] : [retained.get(index) || entry]), removed: true };
}
